import { PaginationState, SortingState, Updater } from "@tanstack/react-table";
import { useMemo } from "react";

import { useIsMobile } from "../../../hooks/useIsMobile";
import { TableDesktop, TableMobile } from "../../../Table";
import { ReturnedTranslatedContentClientsPageHook } from "../../types";
import { useClientFundedNotTradedReportContext } from "../providers/ClientFundedNotTradedProvider";
import { clientFundedNotTradedColumns } from "./Columns";
import { MobileCardBody, MobileCardHeader } from "./MobileCard";

type ClientFundedNotTradedReportTableProps = {
  translations: ReturnedTranslatedContentClientsPageHook;
  isBackoffice?: boolean;
  scaId?: string;
};

export const ClientFundedNotTradedReportTable = ({
  translations,
  scaId,
  isBackoffice,
}: ClientFundedNotTradedReportTableProps) => {
  const { translatedContent, language } = translations;

  const {
    resources: { table },
    state: { pageSize, page, sortOrder, sortBy },
    actions: { setPage, setSort },
    runResource,
  } = useClientFundedNotTradedReportContext();

  const { error, data: tableData, status } = table;
  const { fntList = [], totalCount } = tableData || {};
  const isLoading = status === "loading";

  const isMobile = useIsMobile();

  const sorting = useMemo(
    () =>
      sortBy != null && sortOrder != null
        ? [{ desc: sortOrder === "desc", id: sortBy }]
        : [],
    [sortOrder, sortBy],
  );

  const pageCount = Math.ceil((totalCount ?? 0) / (pageSize ?? 10));

  // Handle pagination changes for server-side mode
  const handlePaginationChange = (
    updater: PaginationState | ((old: PaginationState) => PaginationState),
  ) => {
    const newPagination =
      typeof updater === "function"
        ? updater({
            pageIndex: page,
            pageSize,
          })
        : updater;

    setPage(newPagination.pageIndex);
  };

  const handleSortingUpdate = (updater: Updater<SortingState>) => {
    const nextSorting =
      typeof updater === "function" ? updater(sorting) : updater;
    const newSort = nextSorting[0];
    if (newSort.id !== sortBy) {
      setSort?.(newSort.id, "desc");
    } else {
      setSort?.(nextSorting[0].id, nextSorting[0].desc ? "desc" : "asc");
    }
  };

  const tableConfig = useMemo(
    () => ({
      translatedContent:
        translatedContent.fundedNotTradedTab.fundedNotTradedColumnLabels,
      language,
    }),
    [translatedContent, language],
  );

  const columns = clientFundedNotTradedColumns({
    ...tableConfig,
    isBackoffice,
  });

  if (isMobile) {
    return (
      <TableMobile
        data={fntList || []}
        onRetry={() => runResource("table")}
        isError={Boolean(error)}
        columns={columns}
        cardHeader={MobileCardHeader(tableConfig)}
        cardBody={MobileCardBody(tableConfig)}
        showPagination
        translatedContent={translatedContent}
        scaId={scaId}
        filterFns={{} as any}
        // Server-side pagination props
        isLoading={isLoading}
        enableServerSidePagination={true}
        pageCount={pageCount}
        totalCount={totalCount}
        state={{ pagination: { pageIndex: page, pageSize } }}
        onPaginationChange={handlePaginationChange}
      />
    );
  }

  return (
    <TableDesktop
      data={fntList || []}
      onRetry={() => runResource("table")}
      isError={Boolean(error)}
      columns={columns}
      showPagination
      pageCount={pageCount}
      translatedContent={translatedContent}
      enableSorting
      enabledServerSideSorting
      sorting={sorting}
      onSortingChange={handleSortingUpdate}
      pageSize={pageSize}
      isLoading={isLoading}
      totalCount={totalCount}
      enableServerSidePagination
      onPaginationChange={handlePaginationChange}
      scaId={scaId}
      state={{ pagination: { pageIndex: page, pageSize } }}
      filterFns={{} as any}
    />
  );
};
